import { AUTH } from '@util/firebase';
import { logError } from '@util/logError';

export const MESSAGING_PAUSED_MESSAGE =
  'Your messaging is paused for manual review. You can still buy. Need help? Contact support.';

export class MessagingPausedError extends Error {
  constructor() {
    super(MESSAGING_PAUSED_MESSAGE);
    this.name = 'MessagingPausedError';
  }
}

const activePauses = new Map<string, number>();
const pendingChecks = new Map<string, Promise<void>>();
const PAUSED_CACHE_TTL_MS = 30_000;
const PAUSE_LOOKUP_TIMEOUT_MS = 3_000;

export async function assertMessagingAllowed(senderUid: string): Promise<void> {
  if (!senderUid || AUTH.currentUser?.uid !== senderUid) {
    throw new Error('Sign in again to send a message.');
  }

  // Staff-held pauses can outlive the new-account window; always ask the server.
  const cachedUntil = activePauses.get(senderUid);
  if (cachedUntil && cachedUntil > Date.now()) throw new MessagingPausedError();
  if (cachedUntil) activePauses.delete(senderUid);

  const pending = pendingChecks.get(senderUid);
  if (pending) return pending;

  const check = (async () => {
    const controller = new AbortController();
    let timeoutId: ReturnType<typeof setTimeout> | undefined;
    try {
      const lookup = (async () => {
        // Use the current Firebase token; a tab's auth cookie may be stale.
        const token = await AUTH.currentUser!.getIdToken();
        const response = await fetch('/api/messages/pause', {
          method: 'GET',
          credentials: 'same-origin',
          cache: 'no-store',
          headers: { Authorization: `Bearer ${token}` },
          signal: controller.signal,
        });
        if (!response.ok) throw new Error('Messaging pause lookup failed');
        return response.json() as Promise<{ paused?: boolean; paused_until?: number; remaining_ms?: number }>;
      })();
      const timeout = new Promise<never>((_, reject) => {
        timeoutId = setTimeout(() => {
          controller.abort();
          reject(new Error('Messaging pause lookup timed out'));
        }, PAUSE_LOOKUP_TIMEOUT_MS);
      });
      const result = await Promise.race([lookup, timeout]);
      if (result.paused === true) {
        // The server decides expiry. Device clocks can run hours ahead. Use
        // its remaining duration only for caching, never to overturn a pause.
        const remaining = result.remaining_ms;
        if (typeof remaining === 'number' && Number.isFinite(remaining) && remaining > 0) {
          activePauses.set(senderUid, Date.now() + Math.min(remaining, PAUSED_CACHE_TTL_MS));
        }
        throw new MessagingPausedError();
      }
      if (result.paused !== false) throw new Error('Invalid messaging pause response');
    } catch (error) {
      if (error instanceof MessagingPausedError) throw error;
      // A lookup outage must not take ordinary messaging down. No message,
      // account identifier, or token is included in this diagnostic.
      void logError(`Messaging pause lookup failed; send allowed (${error instanceof Error ? error.name : 'unknown'})`);
    } finally {
      if (timeoutId) clearTimeout(timeoutId);
    }
  })();
  pendingChecks.set(senderUid, check);
  try {
    await check;
  } finally {
    pendingChecks.delete(senderUid);
  }
}
